/**
 * Discrete full convolution (same as numpy.convolve(a, v, mode='full')).
 * out[k] = sum_i a[i] * v[k - i] for valid indices.
 */
export function convolveFull(a: number[], v: number[]): number[] {
  const n = a.length;
  const m = v.length;
  if (n === 0 || m === 0) {
    return [];
  }
  const out = new Array<number>(n + m - 1).fill(0);
  for (let k = 0; k < out.length; k++) {
    let s = 0;
    for (let i = 0; i < n; i++) {
      const j = k - i;
      if (j >= 0 && j < m) {
        s += a[i] * v[j];
      }
    }
    out[k] = s;
  }
  return out;
}

export interface LayerProductionSeries {
  oil: number[];
  liquid: number[];
  /** Water in liquid, % (0–100) */
  wcPct: number[];
}

/**
 * Layer rates from drilling schedule (wells per development year) and single-well base curves.
 * WC% = (liquid - oil) / liquid * 100 when liquid > 0.
 */
export function computeLayerProductionFromSchedule(
  newWellsPerYear: number[],
  baseOil: number[],
  baseLiquid: number[] | null,
): LayerProductionSeries {
  if (!baseOil.length) {
    return { oil: [], liquid: [], wcPct: [] };
  }
  const oil = convolveFull(newWellsPerYear, baseOil);
  const canUseLiquid =
    baseLiquid != null && baseLiquid.length === baseOil.length && baseLiquid.length > 0;
  const liquid = canUseLiquid ? convolveFull(newWellsPerYear, baseLiquid!) : oil.map(() => 0);
  const wcPct = oil.map((o, i) => {
    const l = liquid[i] ?? 0;
    if (l <= 1e-15) {
      return 0;
    }
    const water = Math.max(0, l - o);
    return (water / l) * 100;
  });
  return { oil, liquid, wcPct };
}
